import React, { useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import createLotSchema from '@/schema/create-lot-schema';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn-ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn-ui/card';
import { Label } from '@/components/shadcn-ui/label';
import { Input } from '@/components/shadcn-ui/input';
import { DatePicker } from '@/components/date-picker';
import { Button } from '@/components/shadcn-ui/button';
import { createLot } from '@/network/api.service';
import { useEstateStore } from '@/stores/useEstate.store';
import { cn } from '@/lib/utils';
import { useUserStore } from '@/stores/useUser.store';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const LotCreationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const estates = useEstateStore(state => state.estates);
  const user = useUserStore(state => state.user);
  const [redirecting, setRedirecting] = useState(false);
  const [estateVarietals, setEstateVarietals] = useState([]);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createLotSchema),
    defaultValues: {
      estate_id: '',
      varietal: '',
      weight_of_coffee: '',
      date_of_harvest: new Date(),
      date_of_sourcing: new Date(),
    },
  });

  const onSubmit = async data => {
    try {
      setLoading(true);

      const formatDateToMidnightUTC = date => {
        if (!date) return null;
        const d = new Date(date);
        // Get the date in local timezone to preserve the selected date
        const year = d.getFullYear();
        const month = d.getMonth();
        const day = d.getDate();
        // Create UTC midnight for the selected date
        const midnightUTC = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
        return midnightUTC.toISOString();
      };

      const formattedData = {
        ...data,
        date_of_harvest: data.date_of_harvest ? formatDateToMidnightUTC(data.date_of_harvest) : null,
        date_of_sourcing: data.date_of_sourcing ? formatDateToMidnightUTC(data.date_of_sourcing) : null,
      };

      const respData = await createLot({ ...formattedData, created_by: user?.user_id });

      if (respData?.data?.success) {
        toast.success('Lot created successfully.');
      }

      navigate('/', { replace: true });
    } catch (error) {
      alert('Failed to create');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only redirect after estates have been fetched and the list is empty.
    if (Array.isArray(estates) && estates.length === 0) {
      setRedirecting(true);
      navigate('/estates/add', { replace: true });
    }
  }, [estates, navigate]);

  if (redirecting) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col space-y-[18px] px-4 py-[18px]">
        <Card className="rounded-2xl border border-border shadow-sm">
          <CardHeader className="px-4 pb-3">
            <CardTitle className="text-base font-medium">Lot creation</CardTitle>
          </CardHeader>

          <CardContent className="space-y-3 p-4 pt-0">
            {/* Estate Name */}
            <div className="space-y-1.5">
              <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                Estate<span className="">*</span>
              </Label>

              <Controller
                name="estate_id"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={value => {
                      field.onChange(value);

                      const selectedEstate = estates?.find(est => est._id === value);

                      // update varietal dropdown
                      setEstateVarietals(selectedEstate?.varietal_details || []);

                      // reset varietal when estate changes
                      setValue('varietal', undefined);
                    }}
                  >
                    <SelectTrigger className="rounded-[10px] bg-white">
                      <SelectValue placeholder="Select estate" />
                    </SelectTrigger>

                    <SelectContent>
                      {estates?.map(est => (
                        <SelectItem key={est._id} value={est._id}>
                          {est.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />

              {errors?.estate_id && <div className="text-xs text-red-500">{errors.estate_id.message}</div>}
            </div>

            {/* Varietal Name */}
            <div className="space-y-1.5">
              <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                Varietal<span className="">*</span>
              </Label>

              <Controller
                name="varietal"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange}>
                    <SelectTrigger className="rounded-[10px] bg-white">
                      <SelectValue placeholder="Select varietal" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* <SelectItem value="Arabica">Arabica</SelectItem>
                      <SelectItem value="Robusta">Robusta</SelectItem> */}
                      {estateVarietals?.map(varietal => (
                        <SelectItem key={varietal?._id} value={varietal.varietal}>
                          {varietal.varietal}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors?.varietal && <div className="text-xs text-red-500">{errors.varietal.message}</div>}
            </div>

            {/* Wieght of coffee */}
            <div className="space-y-1.5">
              <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                Weight of coffee (kg)<span className="">*</span>
              </Label>
              <div className="relative">
                <Input
                  disabled={loading}
                  type="number"
                  inputMode="decimal"
                  placeholder="0"
                  step="0.01"
                  className={cn(
                    'rounded-[10px] bg-white text-sm font-normal',
                    errors?.weight_of_coffee && 'border-red-500'
                  )}
                  {...register('weight_of_coffee')}
                />
                {errors?.name && (
                  <CircleAlert size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
                )}
              </div>
              {errors?.weight_of_coffee && (
                <div className="text-xs text-red-500">{errors.weight_of_coffee.message}</div>
              )}
            </div>

            {/* Date of harvest */}
            <div className="space-y-1.5">
              <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                Date of harvest<span className="">*</span>
              </Label>

              <Controller
                name="date_of_harvest"
                control={control}
                render={({ field }) => <DatePicker value={field.value} onChange={field.onChange} />}
              />
            </div>

            {/* Date of sourcing */}
            <div className="space-y-1.5">
              <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                Date of sourcing<span className="">*</span>
              </Label>

              <Controller
                name="date_of_sourcing"
                control={control}
                render={({ field }) => <DatePicker value={field.value} onChange={field.onChange} />}
              />
            </div>
          </CardContent>
        </Card>

        <Button onClick={handleSubmit(onSubmit)} disabled={loading} className="rounded-[10px]">
          {loading ? (
            <>
              <Loader2 className="animate-spin mr-2 h-4 w-4" />
              Creating...
            </>
          ) : (
            'Create Lot'
          )}
        </Button>
      </form>
    </div>
  );
};

export default LotCreationPage;
